import { Head } from "vite-react-ssg";
import { useLocation, useParams } from "react-router-dom";
import type { Region } from "@/i18n";
import {
  buildBlogCollectionJsonLd,
  buildBlogPostingJsonLd,
  buildBreadcrumbJsonLd,
  buildFaqPageJsonLd,
  buildOrganizationJsonLd,
  buildServiceJsonLd,
  getRegionLocale,
  getSiteUrl,
  getSiteUrlOrFallback,
  isServiceRoute,
  resolveRouteMeta,
  type RouteMetaKey,
} from "@/seo/routeMeta";
import {
  OG_DEFAULT,
  OG_IMAGE_HEIGHT,
  OG_IMAGE_TYPE,
  OG_IMAGE_WIDTH,
  resolveOgImage,
  toAbsoluteOgUrl,
} from "@/seo/ogImages";
import { REGION_CODES, REGION_ROUTE_DEFINITIONS, getLocalizedPath, slugFor } from "@/router/routeManifest";
import { getPostBySlug } from "@/content/blog";
import { getAuthor } from "@/content/authors";

const ROUTE_KEY_BY_SLUG: Record<string, RouteMetaKey> = {
  "": "home",
  about: "about",
  abm: "abm",
  "corporate-influencer": "corporateInfluencer",
  "linkedin-ads": "linkedinAds",
  "personal-branding": "personalBranding",
  "hubspot-revops": "hubspotRevops",
  telemarketing: "telemarketing",
  "mail-kampagne": "mailKampagne",
  "mail-campaigns": "mailKampagne",
  "motion-ai": "motionAi",
  "n8n-automations": "n8nAutomations",
  "dm-outbound": "dmOutbound",
  "ip-tracking": "ipTracking",
  einstiegsangebot: "einstiegsangebot",
  "einstiegsangebot/welcome": "einstiegsangebotWelcome",
  booking: "booking",
  "meeting-confirmed": "meetingConfirmed",
  meetingconfirmed: "meetingConfirmedExternal",
  webinare: "webinare",
  "webinar-confirmed": "webinarConfirmed",
  impressum: "impressum",
  datenschutz: "datenschutz",
  resources: "resources",
  blog: "blog",
  "revenue-blueprint": "revenueBlueprint",
  "case-studies": "caseStudies",
  "case-studies/bs-group": "caseStudyBSGroup",
  "case-studies/nexenio": "caseStudyNeXenio",
  "case-studies/saas-crm-plattform": "caseStudySaasCrmPlattform",
  "case-studies/cloud7": "caseStudyCloud7",
  partnerbestellungen: "partnerOrders",
};

function asRegion(value: string | undefined): Region {
  if (value && REGION_CODES.includes(value)) {
    return value as Region;
  }

  return "de";
}

function buildAbsoluteUrl(siteUrl: string, path: string): string {
  if (!siteUrl) {
    return path;
  }

  return `${siteUrl}${path}`;
}

export const SeoHead = () => {
  const location = useLocation();
  const { lang } = useParams();
  const region = asRegion(lang);
  const siteUrl = getSiteUrl();
  const regionLocale = getRegionLocale(region);

  const withoutRegionPrefix = location.pathname.replace(/^\/(de|ch|at|en)/, "");
  const normalizedPath = withoutRegionPrefix.replace(/^\/+/, "").replace(/\/+$/, "");
  const primarySlug = normalizedPath.split("/")[0] || "";
  const routeKey =
    ROUTE_KEY_BY_SLUG[normalizedPath] ?? ROUTE_KEY_BY_SLUG[primarySlug] ?? "notFound";
  const routeMeta = resolveRouteMeta(routeKey, region);

  // Blog ist content-getrieben und vorerst nur unter /de verfügbar.
  const blogSlug = normalizedPath.startsWith("blog/")
    ? normalizedPath.slice("blog/".length)
    : null;
  const isBlogPost = blogSlug !== null;
  const isBlogIndex = primarySlug === "blog" && !isBlogPost;
  const blogPost = blogSlug ? getPostBySlug(blogSlug) : undefined;
  const absoluteBase = getSiteUrlOrFallback();
  // Selbst gehostete Bilder liegen als root-relative Pfade vor; OG/Schema
  // brauchen absolute URLs.
  const toAbsoluteImageUrl = (url: string) =>
    url.startsWith("/") ? `${absoluteBase}${url}` : url;

  const routeDefinition = REGION_ROUTE_DEFINITIONS.find((route) => route.key === routeKey);

  let metaTitle = routeMeta.title;
  let metaDescription = routeMeta.description;
  let noindex = routeMeta.noindex;
  // og:image kommt zentral aus src/seo/ogImages.ts. Routen ohne eigenen Eintrag
  // bekommen OG_DEFAULT — es gibt also nie eine Route ohne Motiv.
  let og = resolveOgImage(`/${normalizedPath}`);
  let ogType = "website";
  let canonicalPath = routeDefinition
    ? getLocalizedPath(region, slugFor(routeDefinition, region))
    : location.pathname;

  if (isBlogIndex) {
    canonicalPath = "/de/blog/";
  } else if (isBlogPost) {
    canonicalPath = `/de/blog/${blogSlug}/`;
    ogType = "article";
    if (blogPost) {
      metaTitle = blogPost.metaTitle ?? blogPost.title;
      metaDescription = blogPost.description;
      noindex = blogPost.draft;
      // Map gewinnt; sonst das Frontmatter-Motiv, sofern es ein renderbares
      // Rasterbild ist. SVG/WebP werden von LinkedIn/Facebook/WhatsApp nicht
      // gerendert und fallen deshalb auf OG_DEFAULT zurück.
      if (og === OG_DEFAULT && blogPost.image && !/\.(svg|webp)$/i.test(blogPost.image)) {
        og = {
          image: toAbsoluteOgUrl(blogPost.image),
          alt: blogPost.imageAlt || OG_DEFAULT.alt,
        };
      }
    } else {
      noindex = true;
    }
  }

  const canonicalUrl = buildAbsoluteUrl(siteUrl, canonicalPath);
  const absoluteCanonicalUrl = `${absoluteBase}${canonicalPath}`;

  const organizationJsonLd = buildOrganizationJsonLd();
  const serviceJsonLd = isServiceRoute(routeKey)
    ? buildServiceJsonLd(routeKey, region, absoluteCanonicalUrl)
    : null;

  const blogPostingJsonLd =
    isBlogPost && blogPost
      ? buildBlogPostingJsonLd({
          title: blogPost.title,
          description: blogPost.description,
          image: og.image,
          datePublished: blogPost.datePublished,
          dateModified: blogPost.dateModified,
          authorName: getAuthor(blogPost.author).name,
          authorUrl: getAuthor(blogPost.author).linkedin,
          authorImage: getAuthor(blogPost.author).image
            ? toAbsoluteImageUrl(getAuthor(blogPost.author).image!)
            : undefined,
          url: absoluteCanonicalUrl,
        })
      : null;
  const faqJsonLd =
    isBlogPost && blogPost && blogPost.faq.length > 0
      ? buildFaqPageJsonLd(blogPost.faq)
      : null;
  const breadcrumbJsonLd =
    isBlogPost && blogPost
      ? buildBreadcrumbJsonLd([
          { name: "Home", url: `${absoluteBase}/de/` },
          { name: "Blog", url: `${absoluteBase}/de/blog/` },
          { name: blogPost.title, url: absoluteCanonicalUrl },
        ])
      : null;
  const collectionJsonLd = isBlogIndex
    ? buildBlogCollectionJsonLd({
        name: metaTitle,
        description: metaDescription,
        url: absoluteCanonicalUrl,
      })
    : null;

  // Blog ist de-only -> keine regionalen hreflang-Alternates.
  const isBlog = isBlogIndex || isBlogPost;
  const alternates =
    routeDefinition && !isBlog
      ? REGION_CODES.map((regionCode) => ({
          hrefLang: regionCode === "en" ? "en" : `de-${regionCode.toUpperCase()}`,
          href: buildAbsoluteUrl(siteUrl, getLocalizedPath(regionCode, slugFor(routeDefinition, regionCode))),
        }))
      : [];

  return (
    <Head>
      <html lang={regionLocale} />
      <title>{metaTitle}</title>
      <meta name="description" content={metaDescription} />
      <meta property="og:title" content={metaTitle} />
      <meta property="og:description" content={metaDescription} />
      <meta property="og:type" content={ogType} />
      <meta property="og:url" content={canonicalUrl} />
      <meta property="og:locale" content={regionLocale} />
      <meta property="og:image" content={og.image} />
      <meta property="og:image:secure_url" content={og.image} />
      <meta property="og:image:width" content={String(OG_IMAGE_WIDTH)} />
      <meta property="og:image:height" content={String(OG_IMAGE_HEIGHT)} />
      <meta property="og:image:type" content={OG_IMAGE_TYPE} />
      <meta property="og:image:alt" content={og.alt} />
      {isBlogPost && blogPost && (
        <meta property="article:published_time" content={blogPost.datePublished} />
      )}
      {isBlogPost && blogPost && (
        <meta property="article:modified_time" content={blogPost.dateModified} />
      )}
      {isBlogPost && blogPost && (
        <meta property="article:author" content={getAuthor(blogPost.author).name} />
      )}
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:title" content={metaTitle} />
      <meta name="twitter:description" content={metaDescription} />
      <meta name="twitter:image" content={og.image} />
      <meta name="twitter:image:alt" content={og.alt} />
      <link rel="canonical" href={canonicalUrl} />
      {isBlog && (
        <link
          rel="alternate"
          type="application/rss+xml"
          title="Prometheus Marketing Blog"
          href={`${absoluteBase}/blog/rss.xml`}
        />
      )}
      {noindex && <meta name="robots" content="noindex, nofollow" />}
      {alternates.map((alternate) => (
        <link key={alternate.hrefLang} rel="alternate" hrefLang={alternate.hrefLang} href={alternate.href} />
      ))}
      {routeDefinition && !isBlog && (
        <link
          rel="alternate"
          hrefLang="x-default"
          href={buildAbsoluteUrl(siteUrl, getLocalizedPath("de", slugFor(routeDefinition, "de")))}
        />
      )}
      <script type="application/ld+json">
        {JSON.stringify(organizationJsonLd)}
      </script>
      {serviceJsonLd && (
        <script type="application/ld+json">
          {JSON.stringify(serviceJsonLd)}
        </script>
      )}
      {blogPostingJsonLd && (
        <script type="application/ld+json">
          {JSON.stringify(blogPostingJsonLd)}
        </script>
      )}
      {faqJsonLd && (
        <script type="application/ld+json">
          {JSON.stringify(faqJsonLd)}
        </script>
      )}
      {breadcrumbJsonLd && (
        <script type="application/ld+json">
          {JSON.stringify(breadcrumbJsonLd)}
        </script>
      )}
      {collectionJsonLd && (
        <script type="application/ld+json">
          {JSON.stringify(collectionJsonLd)}
        </script>
      )}
    </Head>
  );
};
